import { useEffect, useState, useRef } from "react";
import { CButton, CCard, CCardBody, CCardSubtitle, CCardText, CCardTitle, CCol, CFormInput, CFormLabel, CFormSelect, CRow } from "@coreui/react"
import GoogleMapReact from 'google-map-react'
import CookieFunctions from "../functions/CookieFunctions";
import trailerIcon from "../assets/images/trailer.png";
import { toast } from "react-toastify";
import globalFunctions from "../functions/GlobalFunctions";


export default function TrailerLocation() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [orders, setOrders] = useState([]);
    const trailerSearch = useRef();

    // users current location to be updated on user move
    const [userLocation, setUserLocation] = useState(null);
    const [trailer, setTrailer] = useState(null);

    // used for adjusting the map to fit all markers
    const [map, setMap] = useState(null);
    const [maps, setMaps] = useState(null);
    const [markers, setMarkers] = useState([]);

    function getOrders() {
        setLoading(true);
        const myHeaders = new Headers();
        myHeaders.append('Authorization', 'Bearer ' + CookieFunctions.getJWTToken());

        fetch("/api/drivers/trailer/getOrders", {
            headers: myHeaders,
            method: 'GET'
        })
            .then((res) => {
                if (res.ok) {
                    return res.json();
                } else {
                    throw new Error();
                }
            })
            .then((data) => {
                setOrders(data);
            })
            .catch((e) => {
                toast.error(e, {
                    closeButton: true,
                    autoClose: true,
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored"
                });
            }).finally(() => {
                setLoading(false);
            });
    }

    function searchByTrailerNumber(trailerNumber) {
        setLoading(true);
        setError(null);
        setTrailer(null);

        if (trailerNumber === "") {
            setLoading(false)
            return;
        }

        const myHeaders = new Headers();
        myHeaders.append('Authorization', 'Bearer ' + CookieFunctions.getJWTToken());

        fetch(`/api/drivers/trailer/${trailerNumber}/get`, {
            headers: myHeaders,
            method: 'GET'
        })
            .then((res) => {
                if (res.ok) {
                    return res.json();
                } else {
                    return res.text();
                }
            })
            .then((data) => {
                if (typeof data == 'string') {
                    setError(data)
                } else {
                    setTrailer(data);
                }
            })
            .catch((e) => {
                toast.error(e, {
                    closeButton: true,
                    autoClose: true,
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored"
                });
            }).finally(() => {
                setLoading(false);
            });
    }

    function onTrailerChange(e) {
        if (e.target.value === "") {
            setTrailer(null)
            return;
        }

        setTrailer(orders.find((order) => order.orderNumber.toString() === e.target.value));
    }

    function addMarkers() {
        if (!map || !maps) return;

        // clear existing markers
        markers.forEach(marker => marker.setMap(null));
        const userMarker = new maps.Marker({
            position: { lat: userLocation.lat, lng: userLocation.lng },
            map,
            icon: {
                path: maps.SymbolPath.CIRCLE, // Default Google Maps location icon
                scale: 8, // Adjust size
                fillColor: "#4285F4", // Google Maps blue
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: "white",
            },
        })

        const trailerMarker = new maps.Marker({
            position: { lat: trailer.lat, lng: trailer.lng },
            map,
            icon: {
                url: trailerIcon,
                scaledSize: new maps.Size(40, 40),
            }
        })

        // Create a permanent InfoWindow
        const infoWindow = new maps.InfoWindow({
            content: `<div style="
                        font-size: 14px; 
                        font-weight: bold; 
                        padding: 5px;
                        background: white;
                        border-radius: 5px;
                        box-shadow: 2px 2px 10px rgba(0,0,0,0.2);
                        ">
                        ${trailer.trailerNumber}
                        </div>`,
            disableAutoPan: true, // Prevents closing the info window
        });

        // Remove the close button using CSS
        maps.event.addListenerOnce(infoWindow, "domready", () => {
            document.querySelector(".gm-ui-hover-effect").style.display = "none";
        });

        // Open the InfoWindow immediately
        infoWindow.open(map, trailerMarker);

        setMarkers([userMarker, trailerMarker,])
    }

    useEffect(() => {
        getOrders();
        if (navigator.geolocation) {
            const watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const newLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(newLocation);
                },
                (error) => {
                    setError("Please enable location services in your browser settings to continue.")
                    console.error("Error getting location: ", error);
                },
                { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
            );

            return () => navigator.geolocation.clearWatch(watchId);
        }
    }, [])

    // Adjust the map to fit all markers
    useEffect(() => {
        if (map && maps && userLocation && trailer) {
            addMarkers();
            const bounds = new maps.LatLngBounds();

            if (trailer) {
                bounds.extend(new maps.LatLng(trailer.lat, trailer.lng));
            }
            if (userLocation) {
                bounds.extend(new maps.LatLng(userLocation.lat, userLocation.lng));
            }

            map.fitBounds(bounds);
        }
    }, [map, maps, userLocation, trailer]);

    return (
        <CCard className="mb-4">
            <CCardBody>
                <CCardTitle className='fs-2' style={{ paddingBottom: "1%" }}>Trailer Location</CCardTitle>
                <CCardSubtitle style={{ paddingBottom: "1%" }}>DISCLAIMER: For your safety, avoid using your phone while operating this app. Stay alert and use responsibly.  </CCardSubtitle>
                <CRow>
                    <CCol sm="5" xs="12">
                        <CFormLabel className='fs-4'>Search by Order Number</CFormLabel>
                        <CFormSelect
                            name="OrderSelect"
                            value={trailer ? trailer.orderNumber : ""}
                            onChange={onTrailerChange}
                        >
                            <option value="" >Select an order...</option>
                            {orders.map(order => {
                                return <option key={order.orderNumber} value={order.orderNumber}>{`Order# ${order.orderNumber} (Trailer ${order.trailerNumber})`}</option>
                            })}
                        </CFormSelect>
                    </CCol>
                    <CCol sm="2" xs="12" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <CCardText className="fs-4" style={{ backgroundColor: "yellow" }}>OR</CCardText>
                    </CCol>
                    <CCol sm="5 " xs="12">
                        <CRow>
                            <CFormLabel className='fs-4'>Search by Trailer Number</CFormLabel>
                            <CCol sm="8" xs="8">
                                <CFormInput ref={trailerSearch} />
                            </CCol>
                            <CCol sm="2" xs="2">
                                <CButton color="primary" onClick={() => { searchByTrailerNumber(trailerSearch.current.value) }}>Search</CButton>
                            </CCol>
                        </CRow>
                    </CCol>
                    <CCardText style={{ color: 'red', fontWeight: '600', fontSize: '20px', paddingBottom: "3%" }}>{error}</CCardText>
                </CRow>
                {!loading && userLocation && trailer ?
                    <CRow>
                        <CCol xs="12">
                            {/* Order Number */}
                            {trailer.orderNumber !== 0 ? <CRow>
                                <CCardText><b>Order Number:</b> {trailer.orderNumber}</CCardText>
                            </CRow> : null}
                            {/* Trailer Number */}
                            <CRow>
                                <CCardText><b>Trailer Number:</b> {trailer.trailerNumber}</CCardText>
                            </CRow>
                            {/* Trailer Type */}
                            <CRow>
                                <CCardText><b>Trailer Type:</b> {trailer.trailerType}</CCardText>
                            </CRow>
                            {/* Last Update */}
                            <CRow>
                                <CCardText><b>Location Updated At:</b> {globalFunctions.formatDate(trailer.lastUpdated)}</CCardText>
                            </CRow>
                        </CCol>
                        <div style={{ height: '100vh', width: '100%' }}>
                            <GoogleMapReact
                                bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY }}
                                defaultCenter={{
                                    lat: userLocation.lat,
                                    lng: userLocation.lng,
                                }}
                                defaultZoom={11}
                                yesIWantToUseGoogleMapApiInternals
                                onGoogleApiLoaded={({ map, maps }) => {
                                    setMap(map);
                                    setMaps(maps);
                                }}
                                options={{
                                    mapTypeId: "satellite",
                                }}
                            >
                            </GoogleMapReact>
                        </div>
                    </CRow> : null}
            </CCardBody>
        </CCard>
    )
}